<template>
  <div class="products-page cat-page" dir="rtl">
    <div class="products-hero">
      <div>
        <p class="hero-kicker mb-1">مدیریت محتوا و محصولات</p>
        <h1 class="mb-0">دسته‌بندی‌ها</h1>
      </div>
      <div class="hero-side">
        <div class="hero-count">
          <strong>{{ formatNumber(totalCount) }}</strong>
          <span>دسته</span>
        </div>
        <div class="hero-badge"><b-icon icon="diagram-3"></b-icon></div>
      </div>
    </div>

    <div class="products-toolbar">
      <div class="products-toolbar-actions">
        <button type="button" class="products-tool-btn" @click="openCreate()">
          <b-icon icon="plus-lg"></b-icon>
          <span>دسته‌بندی جدید</span>
        </button>
        <router-link class="products-tool-btn" :to="{ name: 'TrashCategory' }">
          <b-icon icon="trash"></b-icon>
          <span>سطل زباله</span>
        </router-link>
        <button type="button" class="products-tool-btn" @click="expandAll" :disabled="!!filter">
          <b-icon icon="arrows-expand"></b-icon>
          <span>نمایش همه زیردسته‌ها</span>
        </button>
        <button type="button" class="products-tool-btn" @click="collapseAll" :disabled="!!filter">
          <b-icon icon="arrows-collapse"></b-icon>
          <span>بستن همه</span>
        </button>
        <LanguageSelectBox class="products-lang" @changeLanguage="onLanguageChange" module-name="category"/>
      </div>

      <div class="products-filter-bar">
        <div class="cat-filter-row">
          <label class="products-filter-field cat-search-field">
            <span><b-icon icon="search"></b-icon> جستجو در دسته‌ها</span>
            <input class="products-filter-input" v-model="searchInput" placeholder="نام یا slug..."
                   @keydown.enter.prevent="applySearchNow">
          </label>
          <button v-if="searchInput" type="button" class="products-more-btn" @click="clearSearch">
            <b-icon icon="x-lg"></b-icon>
            <span>پاک کردن</span>
          </button>
          <span v-if="filter" class="cat-result-count">{{ formatNumber(filter.matches.size) }} نتیجه</span>
        </div>
      </div>
    </div>

    <div class="products-list-panel cat-panel">
      <div v-if="loading" class="products-loading">
        <div class="spinner-border" role="status"></div>
        <p class="mb-0">در حال بارگذاری...</p>
      </div>

      <div v-else-if="!rows.length" class="products-empty">
        <i class="bi bi-diagram-3"></i>
        <p class="mb-0">{{ filter ? 'دسته‌ای با این عبارت پیدا نشد.' : 'هنوز دسته‌ای ثبت نشده است.' }}</p>
        <button v-if="!filter" type="button" class="products-empty-btn" @click="openCreate()">ساخت دسته جدید</button>
      </div>

      <template v-else>
        <div class="cat-tree" role="tree">
          <div v-for="row in renderedRows" :key="row.node.key" class="cat-row" role="treeitem"
               :aria-level="row.node.depth + 1"
               :aria-expanded="row.node.children.length ? String(row.expanded) : null"
               :class="{
                 'is-root': !row.node.depth,
                 'is-match': row.isMatch,
                 'is-context': filter && !row.isMatch,
                 'is-flash': flashKey === row.node.key,
                 'is-hidden': !isActive(row.node.data)
               }">
            <div class="cat-row__main">
              <span v-if="row.node.depth" class="cat-row__guides" aria-hidden="true">
                <span v-for="(rail, i) in row.rails" :key="i" class="cat-rail" :class="{ 'is-line': rail }"></span>
                <span class="cat-elbow" :class="{ 'is-last': row.isLast }"></span>
              </span>
              <button type="button" class="cat-row__toggle" :class="{ 'is-leaf': !row.node.children.length }"
                      :disabled="!row.node.children.length || !!filter"
                      :title="row.node.children.length ? (row.expanded ? 'بستن زیردسته‌ها' : 'نمایش زیردسته‌ها') : null"
                      @click="toggle(row.node.key)">
                <b-icon v-if="row.node.children.length" :icon="row.expanded ? 'chevron-down' : 'chevron-left'"/>
                <span v-else class="cat-row__dot"></span>
              </button>
              <span class="cat-row__thumb">
                <img v-if="row.node.data.image" :src="row.node.data.image" alt="">
                <b-icon v-else :icon="row.node.children.length ? 'folder2-open' : 'tag'"/>
              </span>
              <div class="cat-row__text">
                <div class="cat-row__name">
                  <span class="cat-row__title">
                    <span v-for="(part, i) in highlight(row.node.data.name)" :key="i"
                          :class="{ 'cat-hl': part.m }">{{ part.t }}</span>
                  </span>
                  <span class="cat-depth" :class="'is-d' + Math.min(row.node.depth, 4)">سطح {{ formatNumber(row.node.depth + 1) }}</span>
                  <span v-if="row.node.children.length" class="cat-row__count">
                    {{ formatNumber(row.node.children.length) }} زیردسته
                  </span>
                </div>
                <div v-if="filter && row.isMatch && row.node.parent" class="cat-crumbs">
                  <b-icon icon="signpost-split"/>
                  <template v-for="(name, i) in pathNames(row.node)">
                    <span :key="'n' + i" class="cat-crumb">{{ name }}</span>
                    <span :key="'s' + i" class="cat-crumb-sep">›</span>
                  </template>
                  <span class="cat-crumb is-current">{{ row.node.data.name }}</span>
                </div>
                <div class="cat-row__meta">
                  <span dir="ltr">
                    <span v-for="(part, i) in highlight(row.node.data.slug)" :key="i"
                          :class="{ 'cat-hl': part.m }">{{ part.t }}</span>
                  </span>
                  <span v-if="!filter && row.node.parent" class="cat-row__parent">
                    زیرِ «{{ row.node.parent.data.name }}»
                  </span>
                  <span v-if="!row.expanded && row.node.children.length" class="cat-row__path">
                    زیردسته‌ها بسته است
                  </span>
                </div>
              </div>
            </div>
            <div class="cat-row__stats">
              <router-link v-if="row.node.data.slug" class="cat-stat" v-b-tooltip.hover title="محصولات"
                           :to="{ name: 'SingleCategory', params: { slug: row.node.data.slug } }">
                <b-icon icon="box-seam"/> {{ formatNumber(row.node.data.products_count || 0) }}
              </router-link>
              <span class="cat-stat" v-if="row.node.data.posts_count" v-b-tooltip.hover title="پست‌ها">
                <b-icon icon="newspaper"/> {{ formatNumber(row.node.data.posts_count) }}
              </span>
              <span class="cat-pill" :class="isActive(row.node.data) ? 'is-on' : 'is-off'">
                {{ isActive(row.node.data) ? 'فعال' : 'مخفی' }}
              </span>
            </div>
            <div class="product-actions cat-row__actions">
              <button type="button" class="product-action-btn" v-b-tooltip.hover title="افزودن زیردسته"
                      @click="openCreate({ parent_id: row.node.data.id, language_id: row.node.data.language_id })">
                <b-icon icon="node-plus"/>
              </button>
              <button type="button" class="product-action-btn" v-b-tooltip.hover title="ویرایش سریع"
                      @click="openEdit(row.node)">
                <b-icon icon="pencil"/>
              </button>
              <router-link class="product-action-btn" v-b-tooltip.hover title="صفحه ویرایش"
                           :to="{ name: 'EditCategory', params: { id: row.node.data.id } }">
                <b-icon icon="box-arrow-up-left"/>
              </router-link>
              <button type="button" class="product-action-btn is-danger" v-b-tooltip.hover title="حذف"
                      :disabled="deletingKey === row.node.key" @click="removeCategory(row.node)">
                <b-spinner v-if="deletingKey === row.node.key" small/>
                <b-icon v-else icon="trash"/>
              </button>
            </div>
          </div>
        </div>

        <div v-if="renderLimit < rows.length" ref="sentinel" class="cat-more">
          <button type="button" class="products-more-btn" @click="showMore">
            نمایش {{ formatNumber(Math.min(renderStep, rows.length - renderLimit)) }} مورد دیگر
          </button>
        </div>

        <div class="products-pager" v-if="paginated && !filter && pagination && pagination.last_page > 1">
          <pagination400 :on-change-page="changePage" :meta="pagination"/>
        </div>
      </template>
    </div>

    <b-modal id="category-form-modal" :title="editing ? 'ویرایش دسته‌بندی' : 'دسته‌بندی جدید'" size="xl"
             hide-footer centered scrollable @hidden="onModalHidden">
      <category-form v-if="modalOpen" :category="editing" :records="records" :tree="tree" :defaults="createDefaults"
                     show-cancel @saved="onSaved" @cancel="$bvModal.hide('category-form-modal')"
                     @image-removed="onImageRemoved" @add-translation="onAddTranslation"/>
    </b-modal>
  </div>
</template>

<script>
import {mapGetters} from "vuex";
import LanguageSelectBox from "../../components/dashboard/Language/LanguageSelectBox";
import Pagination400 from "@/plugin/miw-components/pagination/Pagination-400.vue";
import CategoryForm from "../../components/dashboard/category/CategoryForm.vue";
import {
  buildCategoryTree,
  categoryKey,
  categoryPathNames,
  filterCategoryTree,
  mergeCategoryRecords,
  normalizeCategories,
  visibleCategoryRows
} from "../../components/dashboard/category/categoryTree";
import {findAttributeOnSiteInfo} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/FindAttributeOnSiteInfo";
import {formatNumber} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Converter";
import {toast} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/Notification";
import {getAxiosInstance} from "@moeinmdn/mixitweb-front-modules/src/plugin/utils/setInstance.js";
import {queryBuilder} from "@moeinmdn/mixitweb-front-modules/src/plugin/class/QueryFilter";
import "../../components/dashboard/product/products-ui.css";

const RENDER_STEP = 200

export default {
  name: "Category",
  components: {LanguageSelectBox, Pagination400, CategoryForm},
  data() {
    return {
      loading: true,
      records: Object.freeze([]), // ALL categories (flat); the tree is built from these
      pageRootKeys: null, // paginated mode: root keys of the current page (null = show every root)
      collapsed: {},
      searchInput: '',
      searchQuery: '',
      searchTimer: null,
      renderStep: RENDER_STEP,
      renderLimit: RENDER_STEP,
      modalOpen: false,
      editing: null,
      createDefaults: {},
      deletingKey: null,
      flashKey: null,
      flashTimer: null,
      observer: null,
      requestSeq: 0,
      highlightQuery: '',
    }
  },
  computed: {
    ...mapGetters({
      pagination: "Category/getPagination",
    }),
    paginated() {
      const setting = findAttributeOnSiteInfo('dashboard_category_pagination')
      return !(setting && setting[0] == 0)
    },
    // memoized: rebuilt only when `records` is replaced (O(n))
    tree() {
      return buildCategoryTree(this.records)
    },
    filter() {
      return filterCategoryTree(this.tree, this.searchQuery)
    },
    // paginated mode: the page's root categories (each one is rendered with ALL of its descendants)
    pageRoots() {
      if (!this.pageRootKeys) return null
      const byId = this.tree.byId
      const roots = []
      for (let i = 0; i < this.pageRootKeys.length; i++) {
        const node = byId.get(this.pageRootKeys[i])
        if (node && !node.parent) roots.push(node)
      }
      return roots
    },
    rows() {
      const collapsed = this.collapsed
      return visibleCategoryRows(this.tree, {
        isCollapsed: key => !!collapsed[key],
        filter: this.filter,
        roots: this.filter ? null : this.pageRoots, // search always covers every category
      })
    },
    renderedRows() {
      return this.rows.length > this.renderLimit ? this.rows.slice(0, this.renderLimit) : this.rows
    },
    totalCount() {
      return this.tree.size
    },
  },
  watch: {
    searchInput(value) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchQuery = value
      }, 180)
    },
    searchQuery(value) {
      this.renderLimit = RENDER_STEP
      this.highlightQuery = String(value || '').trim().toLowerCase()
    },
    renderedRows() {
      this.$nextTick(this.observeSentinel)
    },
  },
  methods: {
    formatNumber,
    isActive(data) {
      return data.status === 1 || data.status === true || data.status === '1'
    },
    pathNames(node) {
      return categoryPathNames(node)
    },
    // split text into [{t, m}] parts, m = matched search text (rendered as text, no v-html)
    highlight(text) {
      const value = text === null || text === undefined ? '' : String(text)
      const q = this.highlightQuery
      if (!q || !value) return [{t: value, m: false}]
      const lower = value.toLowerCase()
      const parts = []
      let from = 0
      let index = lower.indexOf(q)
      while (index !== -1) {
        if (index > from) parts.push({t: value.slice(from, index), m: false})
        parts.push({t: value.slice(index, index + q.length), m: true})
        from = index + q.length
        index = lower.indexOf(q, from)
      }
      if (from < value.length) parts.push({t: value.slice(from), m: false})
      return parts
    },
    setRecords(list) {
      this.records = Object.freeze(list)
    },
    // full category list, independent of the pagination setting:
    //  - /dashboard/category -> roots with nested children (existing endpoint)
    //  - /dashboard/category/flat-categories?all=1 -> every category flat (newer backend; also hidden children)
    // both are merged by id; an older backend answers the flat call with a 12-item page, which is ignored.
    async fetchAllRecords() {
      const nestedAction = this.paginated ? "Category/getDashboardCategoriesWithoutState" : "Category/getDashboardCategories"
      const filters = (this.$store.state.Category && this.$store.state.Category.filters) || {}
      const [nested, flat] = await Promise.all([
        this.$store.dispatch(nestedAction).catch(e => {
          console.log(e)
          return null
        }),
        getAxiosInstance().get(`/dashboard/category/flat-categories?all=1&${queryBuilder(filters)}`).catch(() => null),
      ])
      const nestedData = nested && nested.data ? nested.data.data : null
      const flatData = flat && flat.data ? flat.data.data : null
      const nestedList = Array.isArray(nestedData) || (nestedData && typeof nestedData === 'object' && !nestedData.meta)
          ? normalizeCategories(nestedData) : null
      const flatList = Array.isArray(flatData) ? normalizeCategories(flatData) : null
      if (!nestedList && !flatList) return null
      return mergeCategoryRecords(nestedList, flatList)
    },
    async getItems(page = 1, {refreshAll = true} = {}) {
      const seq = ++this.requestSeq
      this.loading = refreshAll || !this.records.length
      this.$store.commit("Category/setFilters", {key: "search", value: ''});
      try {
        const [all, pageRes] = await Promise.all([
          refreshAll || !this.records.length ? this.fetchAllRecords() : Promise.resolve(null),
          this.paginated ? this.$store.dispatch("Category/getDashboardCategoriesWithPagination", page) : Promise.resolve(null),
        ])
        if (seq !== this.requestSeq) return
        let list = all || (refreshAll ? [] : this.records.slice())
        if (this.paginated) {
          const pageData = pageRes && pageRes.data && pageRes.data.data
          const pageItems = (pageData && pageData.data) || []
          const pageArr = Array.isArray(pageItems) ? pageItems : Object.values(pageItems)
          list = mergeCategoryRecords(list, normalizeCategories(pageArr))
          this.pageRootKeys = pageArr.filter(item => item && item.id !== undefined).map(item => categoryKey(item.id))
        } else {
          this.pageRootKeys = null
        }
        this.setRecords(list)
        this.renderLimit = RENDER_STEP
      } catch (e) {
        console.log(e)
        if (seq === this.requestSeq && refreshAll) this.setRecords([])
      } finally {
        if (seq === this.requestSeq) this.loading = false
      }
    },
    changePage(page) {
      this.getItems(page, {refreshAll: false})
    },
    onLanguageChange() {
      this.collapsed = {}
      this.getItems(1)
    },
    toggle(key) {
      if (this.collapsed[key]) this.$delete(this.collapsed, key)
      else this.$set(this.collapsed, key, true)
    },
    expandAll() {
      this.collapsed = {}
    },
    collapseAll() {
      const next = {}
      this.tree.byId.forEach(node => {
        if (node.children.length) next[node.key] = true
      })
      this.collapsed = next
    },
    expandAncestors(key) {
      let node = this.tree.byId.get(key)
      node = node ? node.parent : null
      const next = {...this.collapsed}
      while (node) {
        delete next[node.key]
        node = node.parent
      }
      this.collapsed = next
    },
    applySearchNow() {
      clearTimeout(this.searchTimer)
      this.searchQuery = this.searchInput
    },
    clearSearch() {
      this.searchInput = ''
      this.applySearchNow()
    },
    showMore() {
      this.renderLimit += RENDER_STEP
    },
    observeSentinel() {
      if (this.observer) this.observer.disconnect()
      const el = this.$refs.sentinel
      if (!el || typeof IntersectionObserver === 'undefined') return
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.showMore()
      }, {rootMargin: '400px'})
      this.observer.observe(el)
    },
    openCreate(defaults = {}) {
      this.editing = null
      this.createDefaults = {...defaults}
      this.modalOpen = true
      this.$bvModal.show('category-form-modal')
    },
    openEdit(node) {
      this.editing = node.data
      this.createDefaults = {}
      this.modalOpen = true
      this.$bvModal.show('category-form-modal')
    },
    onModalHidden() {
      this.modalOpen = false
      this.editing = null
    },
    flash(key) {
      this.flashKey = key
      clearTimeout(this.flashTimer)
      this.flashTimer = setTimeout(() => {
        this.flashKey = null
      }, 1800)
    },
    // local upsert: replace / append one record, tree is rebuilt in O(n) — no refetch, no reload
    upsertRecord(record) {
      const key = categoryKey(record.id)
      const list = this.records.slice()
      const index = list.findIndex(item => categoryKey(item.id) === key)
      if (index > -1) list.splice(index, 1, {...list[index], ...record})
      else list.push(record)
      this.setRecords(list)
      return key
    },
    onSaved(record, {created}) {
      const key = this.upsertRecord(record)
      if (this.pageRootKeys && categoryKey(record.parent_id) === null && !this.pageRootKeys.includes(key)) {
        this.pageRootKeys = [...this.pageRootKeys, key]
      }
      this.$nextTick(() => {
        this.expandAncestors(key)
        this.flash(key)
      })
      this.$bvModal.hide('category-form-modal')
      if (created) toast('عملیات با موفقیت انجام شد', `دسته «${record.name}» ساخته شد.`, 'success')
    },
    onImageRemoved(category) {
      if (category) this.upsertRecord({id: category.id, image: null})
    },
    onAddTranslation(defaults) {
      this.openCreate(defaults)
    },
    async removeCategory(node) {
      if (!confirm(`دسته «${node.data.name}» حذف شود؟`)) return
      this.deletingKey = node.key
      try {
        await this.$store.dispatch("Category/deleteCategory", node.data)
        // children keep their parent_id and are shown at the root level (orphans)
        const childKeys = node.children.map(child => child.key)
        this.setRecords(this.records.filter(item => categoryKey(item.id) !== node.key))
        if (this.pageRootKeys) {
          // its children become root-level orphans: keep them on the current page
          const keys = this.pageRootKeys.filter(key => key !== node.key)
          childKeys.forEach(key => {
            if (!keys.includes(key)) keys.push(key)
          })
          this.pageRootKeys = keys
        }
        this.$delete(this.collapsed, node.key)
        toast('عملیات با موفقیت انجام شد', 'دسته بندی حذف شد.', 'success')
      } catch (e) {
        console.log(e)
        toast('خطا', 'حذف دسته انجام نشد.', 'danger')
      } finally {
        this.deletingKey = null
      }
    },
    openFromQuery() {
      const q = this.$route.query || {}
      if (!q.create) return
      this.openCreate({language_id: q.lang, name: q.name, description: q.description, parent_id: q.parent_id})
      const query = {...q}
      delete query.create
      delete query.lang
      delete query.name
      delete query.description
      delete query.parent_id
      this.$router.replace({name: this.$route.name, query}).catch(() => {})
    },
  },
  async mounted() {
    this.$store.commit("Category/setFilteredCategories", {});
    await this.getItems()
    this.openFromQuery()
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
    clearTimeout(this.flashTimer)
    if (this.observer) this.observer.disconnect()
  },
  destroyed() {
    this.$store.commit("Category/setFilters", {key: "search", value: ''});
  }
};
</script>

<style scoped>
.products-page {
  padding: 8px 4px 24px;
}

.products-hero {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1e1e1e 0%, #3a3a3a 55%, #eb3d60 140%);
  color: #fff;
  border-radius: 22px;
  padding: 24px 28px;
  margin-bottom: 22px;
}

.hero-kicker {
  opacity: 0.8;
  font-size: 13px;
}

.products-hero h1 {
  font-size: 28px;
  font-weight: 800;
}

.hero-side {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.hero-count {
  text-align: left;
}

.hero-count strong {
  display: block;
  font-size: 26px;
  font-weight: 800;
  line-height: 1.1;
}

.hero-count span {
  font-size: 12px;
  opacity: 0.8;
}

.hero-badge {
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
}

.products-tool-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cat-filter-row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
}

.cat-filter-row .products-more-btn {
  height: 34px;
  margin-top: 0;
}

.cat-search-field {
  flex: 1 1 280px;
}

.cat-result-count {
  align-self: center;
  font-size: 12px;
  font-weight: 700;
  color: #eb3d60;
}

.cat-tree {
  --indent: 22px;
  --row-gap: 4px;
  --guide: #d9d9e0;
  display: flex;
  flex-direction: column;
  gap: var(--row-gap);
}

.cat-row {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 10px 6px 8px;
  border: 1px solid #f0f0f2;
  border-radius: 14px;
  background: #fff;
  transition: background 0.2s ease, box-shadow 0.2s ease;
}

.cat-row.is-root {
  background: #fafafb;
  border-color: #ececf0;
}

.cat-row.is-root .cat-row__name {
  font-size: 0.95rem;
}

.cat-row.is-context {
  opacity: 0.72;
}

/* tree guides: one rail per ancestor level + an elbow (├ / └) for the row itself */
.cat-row__guides {
  display: flex;
  align-self: stretch;
  flex: 0 0 auto;
  margin: -7px 0;
}

.cat-rail,
.cat-elbow {
  position: relative;
  width: var(--indent);
  flex: 0 0 var(--indent);
}

.cat-rail.is-line::before,
.cat-elbow::before {
  content: "";
  position: absolute;
  top: calc(-1 * var(--row-gap));
  bottom: calc(-1 * var(--row-gap));
  right: 50%;
  border-right: 2px solid var(--guide);
}

.cat-elbow.is-last::before {
  bottom: 50%;
}

.cat-elbow::after {
  content: "";
  position: absolute;
  top: 50%;
  left: -6px;
  right: 50%;
  border-top: 2px solid var(--guide);
}

.cat-row.is-match .cat-elbow::before,
.cat-row.is-match .cat-elbow::after {
  border-color: rgba(235, 61, 96, 0.55);
}

.cat-depth {
  flex: 0 0 auto;
  padding: 0 6px;
  border-radius: 999px;
  font-size: 0.66rem;
  font-weight: 700;
  background: rgba(235, 61, 96, 0.1);
  color: #eb3d60;
}

.cat-depth.is-d1 { background: rgba(13, 110, 253, 0.1); color: #0d6efd; }
.cat-depth.is-d2 { background: rgba(25, 135, 84, 0.1); color: #198754; }
.cat-depth.is-d3 { background: rgba(253, 126, 20, 0.12); color: #d9480f; }
.cat-depth.is-d4 { background: #f3f3f5; color: #6b7280; }

.cat-row__title {
  overflow: hidden;
  text-overflow: ellipsis;
}

.cat-hl {
  background: rgba(255, 193, 7, 0.45);
  color: inherit;
  border-radius: 3px;
  padding: 0 1px;
}

.cat-crumbs {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  margin: 1px 0;
  font-size: 0.72rem;
  color: #6b7280;
}

.cat-crumb {
  padding: 0 6px;
  border-radius: 6px;
  background: #f3f3f5;
}

.cat-crumb.is-current {
  background: rgba(235, 61, 96, 0.1);
  color: #eb3d60;
  font-weight: 700;
}

.cat-crumb-sep {
  color: #b0b0b8;
}

.cat-row__parent {
  color: #6b7280;
}

.cat-row:hover {
  background: #fcfcfd;
  box-shadow: 0 6px 16px rgba(30, 30, 30, 0.05);
}

.cat-row.is-hidden .cat-row__name {
  color: #9a9aa5;
}

.cat-row.is-match {
  border-color: rgba(235, 61, 96, 0.35);
  background: rgba(235, 61, 96, 0.04);
}

.cat-row.is-flash {
  animation: cat-flash 1.8s ease;
}

@keyframes cat-flash {
  0% { background: rgba(235, 61, 96, 0.22); }
  100% { background: #fff; }
}

.cat-row__main {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1 1 auto;
  min-width: 0;
}

.cat-row__toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 8px;
  background: #f3f3f5;
  color: #424242;
  cursor: pointer;
}

.cat-row__toggle:disabled {
  cursor: default;
}

.cat-row__toggle.is-leaf {
  background: transparent;
}

.cat-row__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #d4d4da;
}

.cat-row__thumb {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border-radius: 11px;
  overflow: hidden;
  background: rgba(235, 61, 96, 0.08);
  color: #eb3d60;
  font-size: 1.05rem;
}

.cat-row__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.cat-row__text {
  min-width: 0;
}

.cat-row__name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 800;
  font-size: 0.9rem;
  color: #1e1e1e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cat-row__count {
  padding: 0 6px;
  border-radius: 999px;
  background: #f3f3f5;
  color: #6b7280;
  font-size: 0.7rem;
  font-weight: 700;
}

.cat-row__meta {
  display: flex;
  gap: 8px;
  font-size: 0.72rem;
  color: #9a9aa5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.cat-row__path {
  color: #eb3d60;
}

.cat-row__stats {
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
}

.cat-stat {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f6f6f8;
  color: #424242;
  font-size: 0.74rem;
  font-weight: 700;
  text-decoration: none;
}

a.cat-stat:hover {
  background: rgba(235, 61, 96, 0.1);
  color: #eb3d60;
  text-decoration: none;
}

.cat-pill {
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 700;
}

.cat-pill.is-on {
  background: rgba(25, 135, 84, 0.1);
  color: #198754;
}

.cat-pill.is-off {
  background: #f3f3f5;
  color: #8a8a96;
}

.cat-row__actions {
  flex: 0 0 auto;
}

.cat-row__actions button.product-action-btn {
  border: 0;
}

.cat-more {
  display: flex;
  justify-content: center;
  padding: 12px 0 4px;
}

@media (max-width: 767px) {
  .products-page {
    padding: 4px 0 16px;
  }

  .products-hero {
    padding: 14px;
    margin-bottom: 12px;
    border-radius: 16px;
  }

  .hero-badge {
    display: none;
  }

  .products-hero h1 {
    font-size: 22px;
  }

  .cat-tree {
    --indent: 12px;
  }

  .cat-row {
    flex-wrap: wrap;
  }

  .cat-row__main {
    flex: 1 1 100%;
  }

  .cat-row__stats {
    flex: 1 1 auto;
  }
}
</style>
